import React, { useCallback } from 'react';
import { Clock, MapPin, Shield, UserCheck, Users, Palmtree, Mountain, Waves, Sun, Anchor, Ship, Snowflake, Globe } from 'lucide-react';
import { ActivityData, Profession, StatusData, StatusOption, StatusType, OverseasTerritoryId } from '../types';
import { professions, getProfessionPath } from '../data/professions';
import { TEMPORARY_COMPLEMENT_MAJORATION, DOM_TOM_TAX_ABATEMENTS } from '../constants/business';
import { OVERSEAS_TERRITORIES_DATA, findOverseasTerritoryById } from '../types';
import { isTerritoryOpportunitiesAvailable, getTerritoryJobOpportunitiesLink } from '../constants/ctaLinks';

interface ActivityDetailsProps {
  activity: ActivityData;
  onActivityChange: (activity: ActivityData) => void;
  onMarkActivityModified: () => void;
  onProfessionSelect: (profession: Profession) => void;
  status: StatusData;
  onStatusChange: (status: StatusData) => void;
  workTimePercentage: number;
  onWorkTimeChange: (percentage: number) => void;
  selectedOverseasTerritory: OverseasTerritoryId | undefined;
  onOverseasTerritoryChange: (territory: OverseasTerritoryId | undefined) => void;
  selectedProfession: Profession;
}

export default function ActivityDetails({ 
  activity, 
  onActivityChange, 
  onMarkActivityModified,
  onProfessionSelect, 
  status, 
  onStatusChange,
  workTimePercentage,
  onWorkTimeChange,
  selectedOverseasTerritory,
  onOverseasTerritoryChange,
  selectedProfession
}: ActivityDetailsProps) {
  const markActivityModified = useCallback(() => {
    onMarkActivityModified();
  }, [onMarkActivityModified]);

  const handleProfessionSelect = useCallback((profession: Profession) => {
    console.log('🔍 ActivityDetails - handleProfessionSelect called with:', profession);
    console.log('🔍 ActivityDetails - profession.path:', profession.path);
    console.log('🔍 ActivityDetails - current selectedProfession:', selectedProfession);
    onProfessionSelect(profession);
    markActivityModified();
  }, [onProfessionSelect, markActivityModified]);

  const statusOptions: { value: StatusType; label: string; icon: typeof Shield }[] = [
    { value: 'titulaire', label: 'Titulaire', icon: Shield },
    { value: 'stagiaire', label: 'Stagiaire', icon: UserCheck },
    { value: 'contractuel', label: 'Contractuel', icon: Users },
  ];

  const workTimeLabel =
    workTimePercentage === 100 ? 'Temps plein' :
    workTimePercentage === 80 ? '4/5e' :
    workTimePercentage === 60 ? '3/5e' :
    workTimePercentage === 50 ? 'Mi-temps' :
    workTimePercentage === 25 ? 'Quart-temps' :
    `${workTimePercentage}% du temps plein`;

  return (
    <div className="bg-white rounded-xl shadow-sm p-3 md:p-5">
      <div className="space-y-3 md:space-y-4">
          {/* Profession Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Votre profession
            </label>
            <select
              id="profession-select"
              aria-label="Sélectionner votre profession"
              value={selectedProfession.id}
              onChange={(e) => {
                const profession = professions.find(p => p.id === e.target.value);
                if (profession) {
                  handleProfessionSelect(profession);
                }
              }}
              className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            >
              {professions.map((profession) => (
                <option key={profession.id} value={profession.id}>
                  {profession.name} - {profession.category}
                </option>
              ))}
            </select>
          </div>

          {/* Statut professionnel - boutons-icônes compacts */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Statut professionnel
            </label>
            <div className="grid grid-cols-3 gap-2">
              {statusOptions.map(({ value, label, icon: Icon }) => {
                const isActive = status.type === value;
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => onStatusChange({ ...status, type: value })}
                    aria-pressed={isActive}
                    className={`flex flex-col items-center justify-center gap-1 px-2 py-2.5 rounded-lg border-2 transition-colors ${
                      isActive
                        ? 'bg-blue-50 border-blue-500 text-blue-700'
                        : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="text-xs font-medium">{label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Temps de travail - slider compact avec valeur inline */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">
                Temps de travail
              </label>
              <span className="text-sm font-bold text-blue-600">
                {workTimePercentage}% <span className="text-xs font-normal text-gray-500">· {workTimeLabel}</span>
              </span>
            </div>
            <input
              id="work-time-range"
              aria-label="Sélectionner le pourcentage de temps de travail"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={workTimePercentage}
              aria-valuetext={`${workTimePercentage}% du temps plein`}
              type="range"
              min="0"
              max="100"
              step="5"
              value={workTimePercentage}
              onChange={(e) => onWorkTimeChange(Number(e.target.value))}
              className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              style={{
                background: `linear-gradient(to right, #3B82F6 0%, #3B82F6 ${workTimePercentage}%, #E5E7EB ${workTimePercentage}%, #E5E7EB 100%)`
              }}
            />
            <div className="flex justify-between text-[10px] text-gray-500 mt-1">
              <span>0%</span>
              <span>25%</span>
              <span>50%</span>
              <span>75%</span>
              <span>100%</span>
            </div>
          </div>

          {/* Territoire d'affectation */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Territoire d'affectation
            </label>
            <div className="space-y-1.5">
              <button
                onClick={() => onOverseasTerritoryChange(null)}
                className={`w-full px-3 py-2 text-left rounded-lg border transition-colors text-sm font-medium ${
                  selectedOverseasTerritory === null
                    ? 'bg-blue-50 border-blue-400 text-blue-800'
                    : 'bg-gray-50 border-gray-200 hover:bg-gray-100 text-gray-700'
                }`}
              >
                France métropolitaine
              </button>

              <div className="grid grid-cols-2 gap-1.5">
                {OVERSEAS_TERRITORIES_DATA.filter(t => t.id !== null).map((territory) => {
                  const TerritoryIcon = territory.icon;
                  const isActive = selectedOverseasTerritory === territory.id;
                  return (
                    <button
                      key={territory.id}
                      onClick={() => onOverseasTerritoryChange(territory.id)}
                      className={`flex items-center gap-1.5 px-2.5 py-2 rounded-lg border transition-colors ${
                        isActive
                          ? 'bg-orange-50 border-orange-400 text-orange-800'
                          : 'bg-gray-50 border-gray-200 hover:bg-gray-100 text-gray-700'
                      }`}
                    >
                      <TerritoryIcon className="w-4 h-4 flex-shrink-0" />
                      <span className="text-sm font-medium truncate">{territory.name}</span>
                    </button>
                  );
                })}
              </div>

              {selectedOverseasTerritory && (
                (() => {
                  const territoryData = findOverseasTerritoryById(selectedOverseasTerritory);
                  if (!territoryData) return null;

                  const parts: string[] = [`+${territoryData.majoration}% brut`];
                  if (territoryData.complement) parts.push(`+${territoryData.complement}% complément`);
                  if (territoryData.abatementRate) {
                    parts.push(`-${territoryData.abatementRate}% impôts${territoryData.abatementCap ? ` (max ${territoryData.abatementCap}€)` : ''}`);
                  }

                  return (
                    <div className="mt-2 px-3 py-2 bg-orange-50 border border-orange-200 rounded-lg text-xs text-orange-800">
                      <span className="font-medium">Avantages :</span> {parts.join(' · ')}
                      {isTerritoryOpportunitiesAvailable(selectedOverseasTerritory) && (
                        <a
                          href={getTerritoryJobOpportunitiesLink(selectedProfession.id, selectedOverseasTerritory)}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`Découvrir les opportunités d'emploi ${selectedProfession.name} en ${territoryData.name} - Nouvelle fenêtre`}
                          className="ml-2 inline-flex items-center gap-1 text-green-700 hover:text-green-800 font-medium underline"
                        >
                          <MapPin className="w-3 h-3" />
                          Voir les offres
                        </a>
                      )}
                    </div>
                  );
                })()
              )}
            </div>
          </div>
        </div>
    </div>
  );
}